import React, { memo, useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Radio, Space, Tooltip, Typography } from "src/assets/imports/antdComponents";
import { FeatureKeysType, IFolderDeleteModalProps } from "../../models/interfaces";
import "./style.css";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { formatMessage } from "../../transformer";
import { truncateText } from "src/pages/ManageStudies/transformer";
const { Text } = Typography;

const modalStyles = {
	header: { height: "4.5rem", padding: "1.25rem 1.5rem", margin: 0 },
	body: { padding: "1.5rem", maxHeight: "32rem" },
	mask: { backdropFilter: "blur(5px)" },
	footer: { padding: "1rem 1.5rem", paddingTop: "0", margin: 0 },
	content: { padding: 0, margin: 0 },
};

const FolderDeleteModal: React.FC<IFolderDeleteModalProps> = ({ isOpen, folderName, deleteOptions, loading, onDelete, onCancel }) => {
	const { t } = useTranslation();
	const [selectedOption, setSelectedOption] = useState<string | null>(null);
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);
	const featureName = activeFeatures?.find((ele) => ele.feature_key === FeatureKeysType.EnableFolder)?.feature_name;

	// Check if all options are disabled
	const allOptionsDisabled = useMemo(() => deleteOptions.every((option) => !option.button_status), [deleteOptions]);

	// Get warning message from disabled options
	const warningMessage = useMemo(() => {
		if (allOptionsDisabled) {
			const disabledOption = deleteOptions?.find((option) => !option.button_status && option.button_description);
			return disabledOption?.button_description;
		}
		return null;
	}, [allOptionsDisabled, deleteOptions]);

	const isDeleteDisabled = useMemo(() => !selectedOption || loading, [selectedOption, loading]);

	const radioOptions = useMemo(
		() =>
			deleteOptions
				.filter((option) => option.button_status) // Only show enabled options
				.map((option) => (
					<Radio key={option.button_value} value={option.button_value} disabled={loading} className="delete-option-radio-item">
						<div className="flex items-start gap-3">
							<div className="flex-1 min-w-0">
								<Text className="text-base-1 font-medium block text-[--secondary-color]">{t(option.button_text)}</Text>
								{option.button_description && <Text className="text-[x-small] block mt-1 !text-[--secondary-color-2]">{t(option.button_description)}</Text>}
							</div>
						</div>
					</Radio>
				)),
		[deleteOptions, loading, t],
	);

	const handleDelete = useCallback(() => {
		if (selectedOption) {
			onDelete(selectedOption);
		}
	}, [selectedOption, onDelete]);

	const handleOptionChange = useCallback((value: string) => {
		setSelectedOption(value);
	}, []);

	const handleAfterClose = useCallback(() => {
		setSelectedOption(null);
	}, []);

	return (
		<Modal
			title={
				<div className="flex items-center">
					<span>{formatMessage("Delete {featureName}", t, featureName)}</span>
					<span className="border-l-2 border-[--secondary-color-opacity-25] h-6 mx-4"></span>
					<Tooltip title={folderName?.length > 40 ? folderName : ""} overlayInnerStyle={{ maxHeight: "200px", overflow: "auto" }}>
						<span className="text-base-1 text-[--secondary-color-lighter] block">{truncateText(folderName, 40)}</span>
					</Tooltip>
				</div>
			}
			open={isOpen}
			onOk={allOptionsDisabled ? undefined : handleDelete}
			onCancel={onCancel}
			afterClose={handleAfterClose}
			width={allOptionsDisabled ? 500 : 550}
			okText={
				<span className="inline-flex items-center gap-1">
					<i className="material-symbols-outlined text-2xl">delete</i>
					<span>{t("Delete")}</span>
				</span>
			}
			cancelText={t("Cancel")}
			okButtonProps={{
				disabled: isDeleteDisabled,
				danger: true,
				loading: loading,
			}}
			cancelButtonProps={{
				style: allOptionsDisabled ? { display: "none" } : undefined,
			}}
			footer={allOptionsDisabled ? null : undefined}
			centered
			styles={modalStyles}
			className="folder-delete-modal">
			<div className="folder-delete-modal-content">
				<div className={allOptionsDisabled ? "delete-options-error-container" : "delete-options-list-container"}>
					{allOptionsDisabled ? (
						<div className="text-center py-8">
							<i className="material-symbols-outlined text-yellow-600 text-7xl">warning</i>
							<Text className="block mt-2 text-[--warning-color]">{t(warningMessage || "")}</Text>
						</div>
					) : (
						<Radio.Group onChange={(e) => handleOptionChange(e.target.value)} value={selectedOption} className="w-full" disabled={loading}>
							<Space direction="vertical" className="w-full" size={8}>
								{radioOptions.length > 0 ? (
									radioOptions
								) : (
									<div className="text-center py-8">
										<i className="material-symbols-outlined text-[--neutral-grey-opacity-30] text-4xl">delete_forever</i>
										<Text className="block mt-2 text-[--neutral-grey-opacity-60]">{t("No delete options available")}</Text>
									</div>
								)}
							</Space>
						</Radio.Group>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default memo(FolderDeleteModal);
